import { useEffect, useMemo, useRef, useState } from 'react';
import { IconSearch, IconChevronRight } from './icons';
import { logout } from '../lib/auth';
import { triggerPublish } from '../lib/api';
import { useToast } from '../lib/toast';
import type { View } from '../lib/views';

export interface Command {
  id: string;
  label: string;
  hint?: string;
  shortcut?: string;
  group?: string;
  run: () => void | Promise<void>;
}

interface Props {
  open: boolean;
  setOpen: (v: boolean) => void;
  navigate: (view: View) => void;
}

/**
 * Cmd+K command palette. Renders a centered modal with a search input and
 * a list of commands. Up/Down navigates, Enter executes, Esc closes.
 *
 * Commands are static for V1 (navigation + global actions). Extending with
 * dynamic entries (e.g. open fund by name) is straightforward: add them in
 * the `commands` useMemo from app-level state.
 */
export function CommandPalette({ open, setOpen, navigate }: Props) {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  // Focus the input on open and reset query each time.
  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
      setTimeout(() => inputRef.current?.focus(), 10);
    }
  }, [open]);

  const commands = useMemo<Command[]>(() => [
    // Founders section
    { id: 'nav-investors-directory', group: 'Founders', label: 'Investors directory', shortcut: 'G I', run: () => navigate('investors-directory') },
    { id: 'nav-founders',            group: 'Founders', label: 'Founders',            shortcut: 'G F', run: () => navigate('founders') },
    { id: 'nav-founders-inbox',      group: 'Founders', label: 'Founders inbox',                       run: () => navigate('founders-inbox') },
    { id: 'nav-non-dilutive',        group: 'Founders', label: 'Programmes non-dilutifs',              run: () => navigate('non-dilutive') },
    { id: 'nav-brick-pipeline',      group: 'Founders', label: 'Brick pipeline',                       run: () => navigate('brick-pipeline') },
    { id: 'nav-veille',              group: 'Founders', label: 'Veille',                               run: () => navigate('veille') },
    { id: 'nav-templates',           group: 'Founders', label: 'Templates',                            run: () => navigate('templates') },
    { id: 'nav-leads',               group: 'Founders', label: 'Leads',                                run: () => navigate('leads') },
    { id: 'nav-email-funnels',       group: 'Founders', label: 'Email funnels',                        run: () => navigate('email-funnels') },
    // Content section
    { id: 'nav-news',                group: 'Content',  label: 'News',                shortcut: 'G N', run: () => navigate('news') },
    { id: 'nav-quantum-companies',   group: 'Content',  label: 'Quantum Companies',                    run: () => navigate('quantum-companies') },
    // Commun section
    { id: 'nav-dashboard',           group: 'Commun',   label: 'Dashboard',           shortcut: 'G D', run: () => navigate('dashboard') },
    { id: 'nav-activity',            group: 'Commun',   label: 'Activity log',                         run: () => navigate('activity') },
    { id: 'nav-settings',            group: 'Commun',   label: 'Settings',            shortcut: 'G S', run: () => navigate('settings') },

    {
      id: 'publish', group: 'Action', label: 'Publish to financeforquantum.com',
      hint: 'Trigger Netlify build hook',
      run: async () => {
        try { await triggerPublish(); toast.success('Build triggered', 'New deploy starting on Netlify'); }
        catch (e) { toast.error('Publish failed', e instanceof Error ? e.message : String(e)); }
      },
    },
    {
      id: 'open-public', group: 'Action', label: 'Open public site',
      run: () => { window.open('https://financeforquantum.com', '_blank'); },
    },
    {
      id: 'open-airtable', group: 'Action', label: 'Open Airtable base',
      hint: 'Investors directory',
      run: () => { window.open('https://airtable.com/appQjEfx3OTWCnpwd', '_blank'); },
    },
    {
      id: 'open-netlify', group: 'Action', label: 'Open Netlify deploys',
      run: () => { window.open('https://app.netlify.com', '_blank'); },
    },

    { id: 'logout', group: 'Account', label: 'Sign out', run: () => logout() },
  ], [navigate, toast]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return commands;
    return commands.filter(c => `${c.label} ${c.group} ${c.hint ?? ''}`.toLowerCase().includes(q));
  }, [commands, query]);

  // Keep `active` in range of filtered list.
  useEffect(() => { setActive(0); }, [query]);

  function close() { setOpen(false); }
  async function execute(cmd: Command) {
    close();
    try { await cmd.run(); }
    catch (e) { toast.error('Command failed', e instanceof Error ? e.message : String(e)); }
  }

  function onKey(e: React.KeyboardEvent) {
    if (e.key === 'Escape') { e.preventDefault(); close(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(filtered.length - 1, a + 1)); return; }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setActive(a => Math.max(0, a - 1)); return; }
    if (e.key === 'Enter') {
      e.preventDefault();
      const cmd = filtered[active];
      if (cmd) execute(cmd);
      return;
    }
  }

  if (!open) return null;

  // Group filtered commands by group for visual chunks.
  const groups = filtered.reduce<Record<string, Command[]>>((acc, c) => {
    const g = c.group ?? 'Other';
    (acc[g] ??= []).push(c);
    return acc;
  }, {});

  return (
    <div
      className="fixed inset-0 z-[55] bg-navy/30 backdrop-blur-[2px] grid place-items-start pt-[18vh] px-4"
      onClick={close}
    >
      <div
        onClick={e => e.stopPropagation()}
        onKeyDown={onKey}
        className="w-full max-w-[560px] bg-white rounded-xl shadow-pop border border-line overflow-hidden"
      >
        <div className="flex items-center gap-3 px-4 h-12 border-b border-line-soft">
          <IconSearch className="w-4 h-4 text-ink-faint" />
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search commands…"
            className="flex-1 text-[14px] focus:outline-none bg-transparent placeholder:text-ink-faint"
          />
          <kbd className="text-[11px] font-mono text-ink-faint bg-cream-tint border border-line rounded px-1.5 py-0.5">Esc</kbd>
        </div>

        <div className="max-h-[420px] overflow-y-auto scroll-area py-1">
          {filtered.length === 0 && (
            <div className="px-4 py-8 text-center text-[13px] text-ink-muted">No commands match.</div>
          )}
          {Object.entries(groups).map(([group, cmds]) => (
            <div key={group}>
              <div className="px-3 pt-2 pb-1 text-[10.5px] uppercase tracking-wider text-ink-faint font-medium">{group}</div>
              {cmds.map((cmd) => {
                const idx = filtered.indexOf(cmd);
                const isActive = idx === active;
                return (
                  <button
                    key={cmd.id}
                    onClick={() => execute(cmd)}
                    onMouseMove={() => setActive(idx)}
                    className={'w-full flex items-center gap-3 px-3 py-2 text-left text-[13.5px] ' + (isActive ? 'bg-founders-soft text-founders-deep' : 'text-ink hover:bg-cream-tint')}
                  >
                    <IconChevronRight className={'w-3 h-3 ' + (isActive ? 'opacity-100' : 'opacity-30')} />
                    <span className="flex-1 truncate">{cmd.label}</span>
                    {cmd.hint && <span className="text-[12px] text-ink-muted truncate max-w-[160px]">{cmd.hint}</span>}
                    {cmd.shortcut && (
                      <kbd className="text-[10.5px] font-mono text-ink-faint bg-cream-tint border border-line rounded px-1.5 py-0.5">{cmd.shortcut}</kbd>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        <div className="border-t border-line-soft px-4 py-2 flex items-center justify-between text-[11px] text-ink-faint">
          <div className="flex items-center gap-3">
            <span><kbd className="font-mono">↑</kbd> <kbd className="font-mono">↓</kbd> navigate</span>
            <span><kbd className="font-mono">↵</kbd> run</span>
          </div>
          <span><kbd className="font-mono">⌘K</kbd> to open</span>
        </div>
      </div>
    </div>
  );
}
